import { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, GatewayDefinition, Project } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { getErrorMessage } from '@moderepo/mode-app-components';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { ProjectTypeHandlersManager } from './ritzExtensionUtils';
import { DEFAULT_ROUTE_PATHS } from '../containers/routes/AppRoutes';



/**
 * Every where we call useTranslation, we have to check for selected project and use it as namespace
 * and we also change the name from 't' to 'trans'. Therefore we will create this hook to make these
 * easier to use.
 */

// This is the list of terminologies used in Mode platform that might not make sense in some projects.
// So if there is any translation that contain these terms, the terms need to be translated first and then
// we translate the string and pass these terms into the t function.
const ritzTerms: {[key: string]: string} = {
    user_term           : 'terms.user',
    users_term          : 'terms.user_other',
    home_term           : 'terms.home',
    homes_term          : 'terms.home_other',
    device_class_term   : 'terms.device_class',
    device_classes_term : 'terms.device_class_other',
    device_term         : 'terms.device',
    devices_term        : 'terms.device_other',
    member_term         : 'terms.member',
    members_term        : 'terms.member_other',
    sensor_module_term  : 'terms.sensor_module',
    sensor_modules_term : 'terms.sensor_module_other',
    sensor_term         : 'terms.sensor',
    sensors_term        : 'terms.sensor_other',
    gateway_class_term  : 'terms.gateway_class',
    gateway_classes_term: 'terms.gateway_class_other',
    gateway_term        : 'terms.gateway',
    gateways_term       : 'terms.gateway_other',
    entity_class_term   : 'terms.entity_class',
    entity_classes_term : 'terms.entity_class_other',
    entity_term         : 'terms.entity',
    entities_term       : 'terms.entity_other',
};


export const useRitzTranslation = (project: Project | undefined) => {

    const namespace = useMemo((): string | undefined => {
        // set namespace to override when a project has custom translations
        if (project && project.supportToolSettings.translations) {
            return 'override';
        }
        return undefined;
    }, [project]);

    const { t } = useTranslation(namespace);
    const [cachedTranslatedTerms, setCachedTranslatedTerms] = useState({});


    // Because we have a lot of places that has placeholders for terms used by the platform and need to
    // be translated, we will do them here instead. So component will be calling this 'trans' function.
    // We will then call i18n's t function to translation the key and we will pass the translated terms
    // to the t function.
    const trans = useCallback((key: string, ...args: any[]) => {
        const [
            newParams, ...otherArgs
        ] = args;

        return t(key, { ...newParams, ...cachedTranslatedTerms }, ...otherArgs);
    }, [t, cachedTranslatedTerms]);


    /**
     * This use effect is used for monitoring for the namespace changes and translate all the terms for that namespace
     */
    useEffect(() => {
        // Translate the terms and cache it locally
        const newTranslatedTerms = Object.entries(ritzTerms).reduce((result: {[key: string]: string}, entry: string[]): any => {
            const [placeholder, termKey] = entry;
            return {
                ...result,
                [placeholder]: t(termKey),
            };
        }, {});

        setCachedTranslatedTerms(newTranslatedTerms);
    }, [t]);


    return {
        trans,
    };
};


/**
 * Translate the values for the give data object.
 * This is used for cases when we need to pass an object of values to some component and we need to
 * translate the values for this object before we can use it. For example
 *      data: {
 *          button: 'label.ok',
 *          device: 'label.device'
 *      }
 *      Will be translated to
 *      data: {
 *          button: 'Okay',
 *          device: 'Device'
 *      }
 */
export const useTranslateData = (projectId: number | undefined): any => {
    const { state:dataState } = useContext(ritzDataContext);
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const { trans } = useRitzTranslation(project);

    const translateData = useCallback((data: any): any => {
        if (data) {
            const translatedData = Object.entries(data).reduce((result: any, entry: [string, any]) => {
                const [key, value]: [string, any] = entry;
                return {
                    ...result,
                    [key]: (typeof value === 'string') ? trans(value) : value,
                };
            }, {});

            return translatedData;
        }

        return data;
    }, [trans]);

    return translateData;
};


/**
 * Custom hook to get Gateway catalog for a project. We use this in many places therefore we will create a custom hook for it.
 * Because GatewayCatalog is project specific feature therefore this function will need to get the project's handler to
 * handle the function. It will check the handler manager to see if there is a handler for the specified project type
 * and if that handler implemented the getGatewayCatalog function. If the handler does, it will call the handler to get the
 * gateway catalog.
 *
 * @param project
 * @param gatewayOnly
 */
export const useGetProjectGatewayCatalog = (project: Project | undefined) => {
    const [gatewayCatalog, setGatewayCatalog] = useState<readonly GatewayDefinition[] | undefined>();
    const { dispatch:uiDispatch } = useContext(modeUIContext);

    useEffect(() => {
        // Get the Gateway catalog if project handler has implemented this function
        const getGatewayCatalog = async () => {
            if (!gatewayCatalog && project) {
                // Get the AppProxyHandler for the current project
                const handler = ProjectTypeHandlersManager.getInstance().getAppProxyHandler(project.supportToolSettings.projectType);

                if (handler && handler.getGatewayCatalog) {
                    try {
                        const data = await handler.getGatewayCatalog(project.id);
                        setGatewayCatalog(data);
                    } catch (error) {
                        uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                    }
                }
            }
        };

        getGatewayCatalog();
    }, [gatewayCatalog, project, uiDispatch]);

    return gatewayCatalog;
};


/**
 * Get the projectId from the URL pathname if the URL is a project url e.g. /projects/1234/robots/abc. If the URL
 * is not a project url, return undefined.
 * Sometime we can't use useParams() to get the projectId from thee URL because we are outside of a Route. This is
 * a custom hook function to get the projectId from the URL for those cases.
 *
 * @param pathname
 */
export const useGetCurrentProjectId = (pathname: string): number | undefined => {

    const projectId = useMemo(() => {
        // break the pathname into tokens e.g. from "projects/1234/devices/5678/..." to ["projects", "1234", "devices", "5678", ...]
        // If pathname is this "login/google", it will be broken into this ["login", "google"]
        const tokens1 = pathname.substring(1).split('/');

        // Break the route path, "/projects/:projectId", into tokens ["projects", ":projectId"]
        const tokens2 = DEFAULT_ROUTE_PATHS.PROJECTS_$projectId.substring(1).split('/');

        // if the first token from tokens1 and tokens2 match, then we know the pathname is a path for a project
        // therefore we can assume the second token from tokens1 is the projectId
        if (tokens1.length >= 2 && tokens2.length >= 2 && tokens1[0] === tokens2[0]) {
            return Number(tokens1[1]);
        }
        return undefined;
    }, [pathname]);

    return projectId;
};
